import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, Home, CalendarDays, Check, RotateCcw, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function Inquiries() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["inquiries"], queryFn: () => api.get("/inquiries").then((r) => r.data) });

  const toggle = async (it) => {
    await api.patch(`/inquiries/${it.id}?handled=${!it.handled}`);
    qc.invalidateQueries({ queryKey: ["inquiries"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
  };
  const remove = async (id) => {
    await api.delete(`/inquiries/${id}`);
    qc.invalidateQueries({ queryKey: ["inquiries"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    toast.success(t("common.delete"));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">{t("inq.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("inq.subtitle")}</p>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed">
          <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />{t("inq.empty")}
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((it) => (
            <Card key={it.id} className={`p-5 border-border ${it.handled ? "opacity-70" : ""}`} data-testid={`inquiry-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{it.name}</span>
                    {!it.handled ? <Badge className="bg-gold text-[#2A2A2A]">{t("inq.newBadge")}</Badge> : <Badge variant="secondary">{t("inq.handled")}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t("inq.date")} {formatDate(it.created_at)}</div>
                </div>
                {it.room ? <span className="flex items-center gap-1 text-sm font-medium text-teak"><Home className="w-4 h-4" />{it.room}</span> : null}
              </div>

              <div className="mt-3 space-y-1 text-sm">
                {it.email ? <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-3.5 h-3.5" /><a href={`mailto:${it.email}`} className="hover:text-teak">{it.email}</a></div> : null}
                {it.phone ? <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" />{it.phone}</div> : null}
                {it.move_in ? <div className="flex items-center gap-2 text-muted-foreground"><CalendarDays className="w-3.5 h-3.5" />{t("inq.moveIn")}: {formatDate(it.move_in)}</div> : null}
              </div>

              {it.message ? <p className="mt-3 text-sm bg-[#FAFAF8] rounded-md p-3 whitespace-pre-line">{it.message}</p> : null}

              <div className="flex gap-2 mt-4">
                <Button size="sm" variant={it.handled ? "outline" : "default"} className={it.handled ? "" : "bg-teak hover:bg-teak-hover text-white"} onClick={() => toggle(it)} data-testid={`inquiry-toggle-${it.id}`}>
                  {it.handled ? <><RotateCcw className="w-4 h-4 mr-1" />{t("inq.reopen")}</> : <><Check className="w-4 h-4 mr-1" />{t("inq.markHandled")}</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(it.id)} data-testid={`inquiry-delete-${it.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
